<template>
  <div v-if="isLoading" class="flex flex-col justify-center items-center py-20 space-y-4">
    <h1 class="text-xl font-heading">Confirming order...</h1>
    <div class="w-full max-w-sm bg-gray-200 rounded-full h-2 overflow-hidden">
      <div class="bg-primary h-2 rounded-full w-1/4 animate-indeterminate-progress"></div>
    </div>
  </div>

  <div v-else-if="reservationFailed" class="px-4 max-w-[600px] mx-auto py-20">
    <h1 class="text-3xl font-bold font-heading mb-2">Reservation Unsuccessful</h1>
    <template v-if="failedDuringPolling">
      <p class="mb-2">
        We're sorry—something went wrong and your reservation could not be confirmed.
      </p>
      <p class="mb-2 font-semibold">
        Your payment may have been processed. Please contact the venue for assistance.
      </p>
    </template>
    <template v-else>
      <p class="mb-2">We're sorry—the table you selected was just reserved by another guest.</p>
      <p class="mb-2 font-semibold">Your payment was not processed.</p>
      <p>Please try again to choose another available table.</p>
    </template>
    <button
      v-if="!failedDuringPolling"
      @click="handleSelectTables"
      class="mt-8 px-4 py-2 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
      id="select-tables"
    >
      Select Tables
    </button>
  </div>

  <div v-else-if="showSoftMessage" class="px-4 max-w-[600px] mx-auto py-20">
    <h1 class="text-3xl font-bold font-heading mb-4">Your reservation is being processed.</h1>
    <p class="mb-8">
      This is taking longer than usual. Please check your email or refresh the page to check the
      status.
    </p>
    <button
      class="px-4 py-2 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
      @click="handleInitialLoadRetry"
    >
      Check Status
    </button>
  </div>

  <section v-else class="px-4 max-w-screen-xl mx-auto pb-10 pt-10">
    <slot></slot>
  </section>
</template>

<script setup lang="ts">
import useOrderConfirmationStore from '../../stores/OrderConfirmationStore'
import { storeToRefs } from 'pinia'
import { onBeforeMount, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { usePostHog } from '@/common/composables/usePostHog'
import { useCheckoutStore } from '@/stores/checkout'
import { blankError } from '@/utils/errors'

import { ReservationStatus, ReservationStatusType } from '@generated/types'

const POLL_INTERVAL_MS = 4000
const POLLING_TIMEOUT_MS = 90000

const props = defineProps<{
  reservationId: string
  reservationKey: string
}>()

const isLoading = ref<boolean>(true)
const reservationFailed = ref<boolean>(false)
const failedDuringPolling = ref<boolean>(false)
const showSoftMessage = ref<boolean>(false)
const hasReceivedSuccessfulPoll = ref<boolean>(false)
const isFirstPoll = ref<boolean>(true)
const pollCount = ref<number>(0)
let mountTimestamp = 0

const orderConfirmationStore = useOrderConfirmationStore()
const { performance, reservation } = storeToRefs(orderConfirmationStore)

let pollTimerId: ReturnType<typeof setTimeout> | undefined
let softTimeoutId: ReturnType<typeof setTimeout> | undefined

function isReservationInvalid(reservationStatus: ReservationStatusType | undefined): boolean {
  if (!reservationStatus) return false
  return (
    reservationStatus === ReservationStatus.CANCELED ||
    reservationStatus === ReservationStatus.OBSOLETE
  )
}

function isPendingConfirmation(status: ReservationStatusType | undefined): boolean {
  if (!status) return true
  return (
    status === 'authorized' ||
    status === ReservationStatus.UNPAID ||
    status === ReservationStatus.FAILED
  )
}

function stopPolling(): void {
  if (pollTimerId) {
    clearTimeout(pollTimerId)
    pollTimerId = undefined
  }
  if (softTimeoutId) {
    clearTimeout(softTimeoutId)
    softTimeoutId = undefined
  }
}

async function poll(): Promise<void> {
  pollCount.value++
  try {
    if (isFirstPoll.value) {
      await orderConfirmationStore.loadOrderDetails(props.reservationId, props.reservationKey)
      isFirstPoll.value = false
    } else {
      await orderConfirmationStore.refreshReservation(props.reservationId, props.reservationKey)
    }
    if (!reservation.value) {
      captureReservationError('timeout_error', {
        context: 'Initial load completed but no reservation data',
      })
      showSoftMessage.value = true
      isLoading.value = false
      stopPolling()
      return
    }
    const status = reservation.value.status
    if (isReservationInvalid(status)) {
      hasReceivedSuccessfulPoll.value = true
      handleFailedReservation()
      isLoading.value = false
      stopPolling()
      return
    }
    if (!isPendingConfirmation(status)) {
      hasReceivedSuccessfulPoll.value = true
      posthog.capture('Order Confirmed (FE)', {
        reservation_id: props.reservationId,
        poll_count: pollCount.value,
        elapsed_ms: Date.now() - mountTimestamp,
        status,
      })
      isLoading.value = false
      stopPolling()
      return
    }
    if (status === ReservationStatus.FAILED && !failedDuringPolling.value) {
      failedDuringPolling.value = true
      posthog.capture('Reservation Failed - Polling for Recovery (FE)', {
        reservation_id: props.reservationId,
        poll_count: pollCount.value,
        elapsed_ms: Date.now() - mountTimestamp,
        status,
      })
    }
    hasReceivedSuccessfulPoll.value = true
    pollTimerId = setTimeout(poll, POLL_INTERVAL_MS)
  } catch (e) {
    if (!hasReceivedSuccessfulPoll.value) {
      captureReservationError('initial_load_error', {
        error: e instanceof Error ? e.message : 'Unknown error',
      })
      showSoftMessage.value = true
      isLoading.value = false
      stopPolling()
    } else {
      captureReservationError('subsequent_poll_error', {
        error: e instanceof Error ? e.message : 'Unknown error',
      })
      pollTimerId = setTimeout(poll, POLL_INTERVAL_MS)
    }
  }
}

async function startPolling(): Promise<void> {
  stopPolling()
  isLoading.value = true
  showSoftMessage.value = false
  hasReceivedSuccessfulPoll.value = false
  reservationFailed.value = false
  failedDuringPolling.value = false
  isFirstPoll.value = true
  pollCount.value = 0
  mountTimestamp = Date.now()

  softTimeoutId = setTimeout(() => {
    const timeoutStatus = reservation.value?.status
    if (timeoutStatus === ReservationStatus.FAILED) {
      failedDuringPolling.value = true
      handleFailedReservation()
    } else if (isReservationInvalid(timeoutStatus)) {
      handleFailedReservation()
    } else {
      captureReservationError('timeout_error', {
        context: '90 second polling timeout reached',
      })
      showSoftMessage.value = true
    }
    isLoading.value = false
    stopPolling()
  }, POLLING_TIMEOUT_MS)

  await poll()
}

onBeforeMount(() => {
  startPolling()
})

const router = useRouter()
const checkoutError = blankError()
const checkoutStore = useCheckoutStore()
async function handleSelectTables(): Promise<void> {
  if (!performance.value || !reservation.value) {
    await router.replace({ name: 'setGuests' })
    return
  }

  await checkoutStore.setPerformance(performance.value, checkoutError)
  await checkoutStore.setNumberOfGuests(reservation.value.number_of_guests, checkoutError)
  await router.push({ name: 'setTable' })
}

function handleInitialLoadRetry(): void {
  startPolling()
}

onUnmounted(() => {
  stopPolling()
})

const posthog = usePostHog()
function captureReservationError(
  errorType: string,
  additionalData?: Record<string, unknown>,
): void {
  posthog.capture('Reservation Unsuccessful (FE)', {
    reservation_id: props.reservationId,
    error_type: errorType,
    status: reservation.value?.status,
    poll_count: pollCount.value,
    elapsed_ms: Date.now() - mountTimestamp,
    ...additionalData,
  })
}

function handleFailedReservation(): void {
  captureReservationError('reservation_failed')
  reservationFailed.value = true
}
</script>

<style>
@keyframes indeterminate-progress {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(400%);
  }
}

.animate-indeterminate-progress {
  animation: indeterminate-progress 1.5s linear infinite;
}
</style>
